import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
	Button,
	Card,
	Col,
	Divider,
	Flex,
	Form,
	Image,
	Input,
	message,
	notification,
	Radio,
	Row,
	Select,
	Space,
	Tag,
	Tabs,
	Typography,
	Upload,
} from 'antd';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditor } from '@ckeditor/ckeditor5-react';

import uploadApi from '../../api/file';
import projectApi from '../../api/project';
import SH_Model from '../../components/SH_Model';
import Constant from '../../lib/constant';

const { Text } = Typography;

const defaultData = {
	names: { vi: '', en: '' },
	createdAt: new Date(),
	descriptions: { vi: '', en: '' },
	link: { type: '', pageId: '', href: '' },
	image: {},
};

function TV_project_detail(props, ref) {
	const { getProducts, categories } = props;

	const [formInstance, setFormInstance] = useState();
	const [open, setOpen] = useState(false);
	const [index, setIndex] = useState(defaultData);
	const [form] = Form.useForm();
	const refMode = useRef();

	const [initValue, setInitValue] = useState(defaultData);
	const [api, contextHolder] = notification.useNotification();

	const [fileList, setFileList] = useState([]);
	const [uploading, setUploading] = useState(false);
	const [images, setImages] = useState([]);
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState({});
	const [activeLang, setActiveLang] = useState('vi');

	// ✅ lưu path từ record (trường hợp pageId là object {_id, path})
	const [pagePathHint, setPagePathHint] = useState('');

	const propsErrors = useMemo(
		() => ({
			namesvi: null,
			namesen: null,
			linkType: null,
			descriptionsvi: null,
			descriptionsen: null,
			image: null,
		}),
		[]
	);

	const openNotificationWithIcon = (type, messageText, description) => {
		api[type]({ message: messageText, description });
	};

	useImperativeHandle(ref, () => ({
		create: () => {
			setImages([]);
			setFileList([]);
			setErrors(propsErrors);
			setInitValue(defaultData);
			setIndex(defaultData);
			setActiveLang('vi');
			setPagePathHint('');

			setOpen(true);
			refMode.current = Constant.crud.create;
		},
		update: (project) => {
			// ✅ normalize link.pageId: object -> string
			const next = { ...defaultData, ...project };
			const link = structuredClone(next.link || defaultData.link);

			if (link?.type === 'page') {
				// API có thể trả pageId là object {_id, path}
				if (link?.pageId && typeof link.pageId === 'object') {
					const pidObj = link.pageId;
					setPagePathHint(pidObj?.path || '');
					link.pageId = pidObj?._id || '';
				} else {
					setPagePathHint('');
				}
			} else {
				setPagePathHint('');
			}

			next.link = link;

			setInitValue(next);
			setImages(project?.image ? [project.image] : []);
			setIndex({ ...defaultData, ...project, link }); // giữ descriptions từ project
			setFileList([]);
			setErrors(propsErrors);
			setActiveLang('vi');

			setOpen(true);
			refMode.current = Constant.crud.update;
		},
	}));

	useEffect(() => {
		setFormInstance(form);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// ✅ HREF PREVIEW khi chọn PAGE
	const pageHrefPreview = useMemo(() => {
		if (initValue?.link?.type !== 'page') return '';

		const pageId = initValue?.link?.pageId;
		if (!pageId) return pagePathHint || '';

		const p = (categories || []).find((x) => x?._id === pageId);

		// Ưu tiên path (nếu có) -> fallback slug -> fallback hint từ record
		const raw = p?.path || p?.slug || pagePathHint || '';
		if (!raw) return '';

		return raw.startsWith('/') ? raw : `/${raw}`;
	}, [categories, initValue?.link?.pageId, initValue?.link?.type, pagePathHint]);

	const validate = (propsValidate, data) => {
		const _data = structuredClone(
			data || { ...initValue, contents: index.contents, descriptions: index.descriptions, images }
		);
		const _errors = structuredClone(errors);

		if (!propsValidate) propsValidate = propsErrors;

		Object.keys(propsValidate).forEach((prop) => {
			switch (prop) {
				case 'linkType':
					_errors[prop] = _data?.link?.type?.length > 0 ? null : 'Dữ liệu không thể để trống';
					break;
				case 'descriptionsvi':
					_errors[prop] = _data?.descriptions?.vi?.length > 0 ? null : 'Dữ liệu không thể để trống';
					break;
				case 'descriptionsen':
					_errors[prop] = _data?.descriptions?.en?.length > 0 ? null : 'Dữ liệu không thể để trống';
					break;
				case 'namesvi':
					_errors[prop] = _data?.names?.vi?.length > 0 ? null : 'Dữ liệu không thể để trống';
					break;
				case 'namesen':
					_errors[prop] = _data?.names?.en?.length > 0 ? null : 'Dữ liệu không thể để trống';
					break;
				case 'image': {
					const ok = Boolean(images?.[0]?.url || _data?.image?.url);
					_errors[prop] = ok ? null : 'Vui lòng chọn ảnh';
					break;
				}
				default:
					_errors[prop] = _data[prop]?.length > 0 ? null : 'Dữ liệu không thể để trống';
					break;
			}
		});

		setErrors(_errors);
		return Object.values(_errors);
	};

	const handleSubmit = async () => {
		setInitValue({ ...initValue, contents: index.contents, descriptions: index.descriptions, images });

		try {
			const _errorList = validate();
			if (_errorList.filter((x) => x).length === 0) {
				if (refMode.current === Constant.crud.update) {
					setLoading(true);
					const { _id, ...remain } = initValue;

					await projectApi.update(_id, {
						...remain,
						link:
							remain?.link?.type === 'url'
								? { type: 'url', href: remain?.link?.href }
								: { type: 'page', pageId: remain?.link?.pageId }, // ✅ pageId string
						descriptions: index.descriptions,
					});

					openNotificationWithIcon('success', 'Thành công', 'Thao tác thành công');
					onCancel();
					getProducts();
					setLoading(false);
				} else {
					setLoading(true);

					await projectApi.create({
						...initValue,
						link:
							initValue?.link?.type === 'url'
								? { type: 'url', href: initValue?.link?.href }
								: { type: 'page', pageId: initValue?.link?.pageId }, // ✅ pageId string
						descriptions: index.descriptions,
					});

					openNotificationWithIcon('success', 'Thành công', 'Thao tác thành công');
					onCancel();
					getProducts();
					setLoading(false);
				}
			}
		} catch (err) {
			console.log(err);
		} finally {
			setLoading(false);
		}
	};

	const handleRemoveImage = async (img) => {
		setImages(images.filter((_img) => _img.public_id !== img.public_id));
	};

	const onCancel = () => {
		setOpen(false);
		refMode.current = '';
		formInstance?.resetFields();
		setInitValue(defaultData);
		setIndex(defaultData);
		setErrors(propsErrors);
		setFileList([]);
		setImages([]);
		setActiveLang('vi');
		setPagePathHint('');
	};

	function uploadPlugin(editor) {
		editor.plugins.get('FileRepository').createUploadAdapter = (loader) => uploadAdapter(loader);
	}

	function uploadAdapter(loader) {
		return {
			upload: () =>
				new Promise(async (resolve, reject) => {
					try {
						const file = await loader.file;
						if (file) {
							uploadApi
								.uploadImage(file)
								.then((data) => {
									resolve({ default: data.data.data.url });
									message.success('upload successfully.');
								})
								.catch(() => {
									message.error('upload failed.');
								});
						}
					} catch (error) {
						reject('Error');
					}
				}),
			abort: () => {},
		};
	}

	const handleUpload = async () => {
		setUploading(true);
		const response = await uploadApi.uploadImage(fileList[0]);
		setImages([response.data.data]);
		setInitValue((prev) => ({ ...prev, image: response.data.data }));
		message.success('upload successfully.');
		setUploading(false);
	};

	const uploadProps = {
		onRemove: () => setFileList([]),
		beforeUpload: (file) => {
			setFileList([file]);
			return false;
		},
		fileList,
	};

	const isCreate = refMode.current === Constant.crud.create;

	const langTabs = [
		{
			key: 'vi',
			label: 'Tiếng Việt',
			children: (
				<Space direction="vertical" size={12} style={{ width: '100%' }}>
					<Form.Item
						label="Tên dự án (VI)"
						validateStatus={errors['namesvi'] ? 'error' : ''}
						help={errors['namesvi']}
					>
						<Input
							placeholder="Nhập tên tiếng Việt..."
							value={initValue.names?.vi}
							onChange={(e) =>
								setInitValue((prev) => ({
									...prev,
									names: { vi: e.target.value, en: prev.names.en },
								}))
							}
						/>
					</Form.Item>

					<Form.Item
						label="Mô tả (VI)"
						validateStatus={errors['descriptionsvi'] ? 'error' : ''}
						help={errors['descriptionsvi']}
					>
						<Input.TextArea
							rows={6}
							placeholder="Nhập mô tả tiếng Việt..."
							value={index?.descriptions?.vi || ''}
							onChange={(e) => {
								const text = e.target.value;
								setIndex((prev) => ({
									...prev,
									descriptions: { vi: text, en: prev.descriptions.en },
								}));
							}}
						/>
					</Form.Item>
				</Space>
			),
		},
		{
			key: 'en',
			label: 'English',
			children: (
				<Space direction="vertical" size={12} style={{ width: '100%' }}>
					<Form.Item
						label="Project name (EN)"
						validateStatus={errors['namesen'] ? 'error' : ''}
						help={errors['namesen']}
					>
						<Input
							placeholder="Enter English name..."
							value={initValue.names?.en}
							onChange={(e) =>
								setInitValue((prev) => ({
									...prev,
									names: { en: e.target.value, vi: prev.names.vi },
								}))
							}
						/>
					</Form.Item>

					<Form.Item
						label="Description (EN)"
						validateStatus={errors['descriptionsen'] ? 'error' : ''}
						help={errors['descriptionsen']}
					>
						<Input.TextArea
							rows={6}
							placeholder="Enter English description..."
							value={index?.descriptions?.en || ''}
							onChange={(e) => {
								const text = e.target.value;
								setIndex((prev) => ({
									...prev,
									descriptions: { en: text, vi: prev.descriptions.vi },
								}));
							}}
						/>
					</Form.Item>
				</Space>
			),
		},
	];

	return (
		<>
			{contextHolder}

			<SH_Model
				open={open}
				handleSubmit={handleSubmit}
				onCancel={onCancel}
				textOk={isCreate ? 'Tạo mới' : 'Cập nhật'}
				loading={loading}
				title={
					<Flex align="center" justify="space-between" wrap="wrap" gap={8}>
						<Flex align="center" gap={8} wrap="wrap">
							<span style={{ fontWeight: 600 }}>{isCreate ? 'Tạo mới dự án' : 'Cập nhật dự án'}</span>
							{isCreate ? <Tag color="blue">CREATE</Tag> : <Tag color="gold">UPDATE</Tag>}
						</Flex>
					</Flex>
				}
			>
				<div style={{ maxHeight: '70vh', overflow: 'auto', paddingRight: 6 }}>
					<Form form={form} layout="vertical">
						<Row gutter={[12, 12]}>
							{/* LEFT */}
							<Col xs={24} lg={16}>
								<Card
									size="small"
									title={
										<Flex align="center" justify="space-between">
											<span style={{ fontWeight: 600 }}>Thông tin dự án</span>
											<Text type="secondary" style={{ fontSize: 12 }}>
												Chỉnh theo ngôn ngữ
											</Text>
										</Flex>
									}
									style={{ borderRadius: 14, padding: 12 }}
								>
									<Tabs activeKey={activeLang} onChange={setActiveLang} items={langTabs} />
								</Card>

								<Card
									size="small"
									title={<span style={{ fontWeight: 600 }}>Liên kết</span>}
									style={{ borderRadius: 14, marginTop: 12, padding: 12 }}
								>
									<Row gutter={[12, 12]} align="middle">
										<Col xs={24} md={12}>
											<Form.Item
												label="Link Type"
												validateStatus={errors['linkType'] ? 'error' : ''}
												help={errors['linkType']}
											>
												<Radio.Group
													value={initValue.link?.type}
													optionType="button"
													buttonStyle="solid"
													onChange={(e) => {
														const _data = structuredClone(initValue);
														_data.link.type = e.target.value;

														// reset preview hint khi đổi type
														if (e.target.value !== 'page') setPagePathHint('');

														setInitValue(_data);
													}}
												>
													<Radio.Button value="url">URL</Radio.Button>
													<Radio.Button value="page">PAGE</Radio.Button>
												</Radio.Group>
											</Form.Item>
										</Col>

										<Col xs={24} md={12}>
											<Form.Item shouldUpdate={(p, c) => p?.link?.type !== c?.link?.type} noStyle>
												{() => {
													const type = initValue.link?.type;
													if (!type) return null;

													return (
														<>
															{type === 'url' ? (
																<Form.Item label="href">
																	<Input
																		placeholder="/contact hoặc https://..."
																		value={initValue?.link?.href}
																		onChange={(e) =>
																			setInitValue((prev) => ({
																				...prev,
																				link: {
																					...prev.link,
																					type: prev.link?.type,
																					href: e.target.value,
																				},
																			}))
																		}
																	/>
																</Form.Item>
															) : (
																<>
																	<Form.Item label="pageId">
																		<Select
																			style={{ width: '100%' }}
																			fieldNames={{
																				label: 'label',
																				value: '_id',
																			}}
																			value={initValue.link?.pageId}
																			options={categories}
																			placeholder="Chọn page..."
																			allowClear
																			showSearch
																			optionFilterProp="label"
																			onChange={(val) => {
																				// val là string _id
																				setInitValue((prev) => ({
																					...prev,
																					link: { ...prev.link, pageId: val },
																				}));

																				const p = (categories || []).find(
																					(x) => x?._id === val
																				);
																				const raw = p?.path || p?.slug || '';
																				setPagePathHint(
																					raw
																						? raw.startsWith('/')
																							? raw
																							: `/${raw}`
																						: ''
																				);
																			}}
																		/>
																	</Form.Item>

																	<Form.Item label="path / href (preview)">
																		<Input
																			readOnly
																			value={pageHrefPreview}
																			placeholder="Chưa chọn page"
																			style={{ borderRadius: 10 }}
																		/>
																		{pageHrefPreview ? (
																			<Text
																				type="secondary"
																				style={{ fontSize: 12 }}
																				copyable={{ text: pageHrefPreview }}
																			>
																				Copy đường dẫn
																			</Text>
																		) : null}
																	</Form.Item>
																</>
															)}
														</>
													);
												}}
											</Form.Item>
										</Col>
									</Row>
								</Card>
							</Col>

							{/* RIGHT */}
							<Col xs={24} lg={8}>
								<Card
									size="small"
									title={<span style={{ fontWeight: 600 }}>Hình ảnh</span>}
									style={{ borderRadius: 14, padding: 12 }}
								>
									<Space direction="vertical" size={12} style={{ width: '100%' }}>
										<Text type="secondary" style={{ fontSize: 12 }}>
											Chọn 1 ảnh đại diện. Ảnh sẽ hiển thị ở landing.
										</Text>

										<Space wrap>
											<Upload {...uploadProps} maxCount={1} showUploadList>
												<Button icon={<UploadOutlined />}>Chọn ảnh</Button>
											</Upload>

											<Button
												type="primary"
												onClick={handleUpload}
												disabled={fileList.length === 0}
												loading={uploading}
											>
												{uploading ? 'Đang tải' : 'Tải lên'}
											</Button>
										</Space>

										{errors['image'] ? <Text type="danger">{errors['image']}</Text> : null}

										<Divider style={{ margin: '8px 0' }} />

										{images?.length > 0 ? (
											<Flex gap={10} wrap="wrap">
												{images.map((img, idx) => (
													<div
														key={idx}
														style={{
															position: 'relative',
															width: '100%',
															maxWidth: 320,
															borderRadius: 14,
															overflow: 'hidden',
															border: '1px solid #f0f0f0',
														}}
													>
														<Image
															width="100%"
															height={180}
															style={{ objectFit: 'cover' }}
															src={img?.url}
															preview
														/>
														<Button
															danger
															size="small"
															icon={<DeleteOutlined />}
															onClick={() => handleRemoveImage(img)}
															style={{
																position: 'absolute',
																top: 8,
																right: 8,
																borderRadius: 999,
																boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
															}}
														/>
													</div>
												))}
											</Flex>
										) : (
											<div
												style={{
													width: '100%',
													height: 180,
													borderRadius: 14,
													border: '1px dashed #d9d9d9',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													color: '#999',
												}}
											>
												Chưa có ảnh
											</div>
										)}
									</Space>
								</Card>
							</Col>
						</Row>
					</Form>
				</div>
			</SH_Model>
		</>
	);
}

export default forwardRef(TV_project_detail);
